import { ApiError } from '../../api/client';
import type { Network, NetworkMatrix, NetworkReach, PolicyVersion } from '../../api/types';
import { errorText } from '../../lib/useRelay';

/** Addresses in a CIDR: 2^(32 - prefix). A malformed one counts as 0. */
export function addressesIn(cidr: string): number {
  const m = /\/(\d{1,2})$/.exec(cidr.trim());
  const bits = m ? Number(m[1]) : NaN;
  return Number.isInteger(bits) && bits >= 0 && bits <= 32 ? 2 ** (32 - bits) : 0;
}

/** Usable host addresses: all but the network and broadcast address. */
export function hostsIn(cidr: string): number {
  return Math.max(0, addressesIn(cidr) - 2);
}

/** What a card says is free: hosts in the CIDR − 2 − devices, never below 0. */
export function freeAddresses(n: Pick<Network, 'cidr' | 'deviceCount'>): number {
  return Math.max(0, hostsIn(n.cidr) - n.deviceCount);
}

/** The usage bar's fill, 0..1; a sliver stays visible for an empty network. */
export function usage(n: Pick<Network, 'cidr' | 'deviceCount'>): number {
  const hosts = hostsIn(n.cidr);
  return hosts ? Math.min(1, Math.max(0.02, n.deviceCount / hosts)) : 0;
}

/**
 * The id POST /networks derives from a name (CONTRACT 3.4): lowercased, every run of characters
 * outside [a-z0-9-] → "-", "-" trimmed, cut to 32.
 */
export function deriveId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

export const ID_PATTERN = /^[a-z0-9-]{1,32}$/;

/** A matrix cell as the grid draws it: the diagonal reads "within" whatever its reach. */
export type CellKind = NetworkReach | 'within';

export interface GridCell {
  source: string;
  destination: string;
  kind: CellKind;
  reach: NetworkReach;
  /** "#3", or the raw id when the active policy does not hold it. */
  rules: string[];
  /** The tooltip: what decides this pair. */
  title: string;
}

export interface GridRow {
  id: string;
  name: string;
  cells: GridCell[];
}

export interface Grid {
  /** Destinations, in matrix order. */
  columns: { id: string; name: string }[];
  rows: GridRow[];
  /** "v14", or "open policy" for version 0. */
  versionLabel: string;
  open: boolean;
}

const REACH_WORD: Record<NetworkReach, string> = { allow: 'Allow', partial: 'Partial', deny: 'Deny' };

export const cellLabel = (c: Pick<GridCell, 'kind'>) => (c.kind === 'within' ? 'within' : REACH_WORD[c.kind]);

/** Rule ids → "#N" by their place in the active policy (1-based, as the Firewall screen numbers them). */
export function ruleNumbers(ruleIds: readonly string[], policy: Pick<PolicyVersion, 'rules'> | null | undefined): string[] {
  const at = new Map((policy?.rules ?? []).map((r, i) => [r.id, i + 1]));
  return ruleIds.map((id) => {
    const n = at.get(id);
    return n ? `#${n}` : id;
  });
}

function cellTitle(source: string, destination: string, reach: NetworkReach, rules: string[], open: boolean): string {
  const pair = source === destination ? `Within ${source}` : `${source} → ${destination}`;
  if (open) return `${pair}: ${REACH_WORD[reach]} (open policy, nothing published yet)`;
  if (!rules.length) return `${pair}: ${REACH_WORD[reach]}, no rule matches (default deny)`;
  return `${pair}: ${REACH_WORD[reach]}, rule${rules.length === 1 ? '' : 's'} ${rules.join(', ')}`;
}

/** GET /networks/matrix → rows of sources, columns of destinations, with network names and rule numbers. */
export function toGrid(
  matrix: NetworkMatrix,
  networks: readonly Pick<Network, 'id' | 'name'>[] | null | undefined,
  policy: Pick<PolicyVersion, 'rules'> | null | undefined,
): Grid {
  const nameOf = new Map((networks ?? []).map((n) => [n.id, n.name]));
  const name = (id: string) => nameOf.get(id) ?? id;
  const open = matrix.version === 0;
  const byPair = new Map(matrix.cells.map((c) => [`${c.source}\u0000${c.destination}`, c]));
  const columns = matrix.networks.map((id) => ({ id, name: name(id) }));
  const rows = matrix.networks.map((source) => ({
    id: source,
    name: name(source),
    cells: matrix.networks.map((destination): GridCell => {
      const c = byPair.get(`${source}\u0000${destination}`);
      const reach = c?.reach ?? 'deny';
      const rules = ruleNumbers(c?.ruleIds ?? [], policy);
      return {
        source,
        destination,
        kind: source === destination ? 'within' : reach,
        reach,
        rules,
        title: cellTitle(source, destination, reach, rules, open),
      };
    }),
  }));
  return { columns, rows, versionLabel: open ? 'open policy' : `v${matrix.version}`, open };
}

export type NetworkField = 'name' | 'id' | 'cidr' | 'note';

/** An API error from a network call, placed on the form field it is about, or the dialog when none. */
export interface PlacedError {
  field: NetworkField | null;
  message: string;
}

/**
 * network_overlap and a full overlay (conflict) belong to the CIDR, network_exists to the id,
 * network_in_use to the dialog (its message names the reason and the rule #s). validation_failed
 * goes to the field its message names.
 */
export function networkError(e: unknown): PlacedError {
  if (!(e instanceof ApiError)) return { field: null, message: errorText(e) };
  switch (e.code) {
    case 'network_overlap':
      return { field: 'cidr', message: e.message || 'This range overlaps another network.' };
    case 'network_exists':
      return { field: 'id', message: 'A network with this id already exists. Pick another id.' };
    case 'network_in_use':
      return { field: null, message: e.message || 'The network is still in use.' };
    case 'conflict':
      return { field: 'cidr', message: e.message || 'No free /24 is left in the overlay.' };
    case 'validation_failed': {
      const m = e.message;
      const field: NetworkField | null = /\bcidr\b/i.test(m)
        ? 'cidr'
        : /\bid\b/i.test(m)
          ? 'id'
          : /\bnote\b/i.test(m)
            ? 'note'
            : /\bname\b/i.test(m)
              ? 'name'
              : null;
      return { field, message: m || 'The server rejected this value.' };
    }
    default:
      return { field: null, message: errorText(e) };
  }
}

export interface NetworkForm {
  name: string;
  id: string;
  /** False until the id field is typed in; until then it follows the name. */
  idEdited: boolean;
  cidr: string;
  isolated: boolean;
  note: string;
}

export function blankForm(nextFreeCidr: string | null): NetworkForm {
  return { name: '', id: '', idEdited: false, cidr: nextFreeCidr ?? '', isolated: false, note: '' };
}

export function formOf(n: Network): NetworkForm {
  return { name: n.name, id: n.id, idEdited: true, cidr: n.cidr, isolated: n.isolated, note: n.note ?? '' };
}

/** Client-side checks, the same rules POST /networks applies; the server stays the judge. */
export function validateForm(f: NetworkForm, creating: boolean): Partial<Record<NetworkField, string>> {
  const errors: Partial<Record<NetworkField, string>> = {};
  const name = f.name.trim();
  if (!name) errors.name = 'Name is required.';
  else if (name.length > 64) errors.name = 'At most 64 characters.';
  if (f.note.length > 200) errors.note = 'At most 200 characters.';
  if (creating) {
    if (!ID_PATTERN.test(f.id)) errors.id = 'Lowercase letters, digits and "-", 1 to 32 characters.';
    if (f.cidr.trim() && !/^\d{1,3}(\.\d{1,3}){3}\/(2[4-9]|30)$/.test(f.cidr.trim())) {
      errors.cidr = 'A /24 to /30 inside 10.88.0.0/16, e.g. 10.88.4.0/24.';
    }
  }
  return errors;
}

/** POST /networks body: the id as shown, the CIDR when one is filled in, a note only when written. */
export function createBody(f: NetworkForm) {
  const cidr = f.cidr.trim();
  const note = f.note.trim();
  return {
    name: f.name.trim(),
    id: f.id,
    ...(cidr ? { cidr } : {}),
    isolated: f.isolated,
    note: note || null,
  };
}

/** PATCH /networks/:id body: only what changed; empty = nothing to save. */
export function patchBody(n: Network, f: NetworkForm) {
  const out: { name?: string; isolated?: boolean; note?: string | null } = {};
  const name = f.name.trim();
  const note = f.note.trim() || null;
  if (name !== n.name) out.name = name;
  if (f.isolated !== n.isolated) out.isolated = f.isolated;
  if (note !== n.note) out.note = note;
  return out;
}
